import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster, toast } from "sonner";
import { useEffect, useRef } from "react";
import { AuthProvider, useAuth } from "@/lib/AuthContext";
import { apiAuth, setToken } from "@/lib/api";
import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import ActivityDetail from "@/pages/ActivityDetail";
import CreateActivity from "@/pages/CreateActivity";
import Live from "@/pages/Live";
import Chats from "@/pages/Chats";
import ChatDetail from "@/pages/ChatDetail";
import Crews from "@/pages/Crews";
import CrewDetail from "@/pages/CrewDetail";
import Profile from "@/pages/Profile";
import Notifications from "@/pages/Notifications";
import Tokens from "@/pages/Tokens";
import Referrals from "@/pages/Referrals";
import Venues from "@/pages/Venues";
import VenueDetail from "@/pages/VenueDetail";
import Admin from "@/pages/Admin";
import Business from "@/pages/Business";
import BusinessSignup from "@/pages/BusinessSignup";
import AppLayout from "@/components/AppLayout";

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const nav = useNavigate();
  const { signIn } = useAuth();
  const handled = useRef(false);

  useEffect(() => {
    if (handled.current) return;
    handled.current = true;
    const hash = window.location.hash || "";
    const m = hash.match(/session_id=([^&]+)/);
    if (!m) { nav("/", { replace: true }); return; }
    const session_id = decodeURIComponent(m[1]);
    apiAuth.googleSession(session_id)
      .then((res) => {
        setToken(res.token);
        signIn(res.token, res.user);
        // Clear the hash and route to the feed
        window.history.replaceState(null, "", window.location.pathname);
        toast.success(`Welcome, ${res.user.name}`);
        nav("/feed", { replace: true });
      })
      .catch(() => {
        toast.error("Google sign-in failed");
        nav("/login", { replace: true });
      });
  }, [nav, signIn]);

  return <div className="min-h-screen flex items-center justify-center text-sm text-[#555]">Signing you in…</div>;
};

const AppRouter = () => {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/onboarding" element={<Onboarding />} />
      <Route element={<AppLayout />}>
        <Route path="/feed" element={<Home />} />
        <Route path="/live" element={<Live />} />
        <Route path="/chats" element={<Chats />} />
        <Route path="/crews" element={<Crews />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/:id" element={<Profile />} />
        <Route path="/notifications" element={<Notifications />} />
        <Route path="/tokens" element={<Tokens />} />
        <Route path="/referrals" element={<Referrals />} />
        <Route path="/venues" element={<Venues />} />
        <Route path="/venues/:id" element={<VenueDetail />} />
        <Route path="/activity/:id" element={<ActivityDetail />} />
        <Route path="/create" element={<CreateActivity />} />
        <Route path="/chats/:id" element={<ChatDetail />} />
        <Route path="/crews/:id" element={<CrewDetail />} />
      </Route>
      <Route path="/admin" element={<Admin />} />
      <Route path="/business" element={<Business />} />
      <Route path="/business/signup" element={<BusinessSignup />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <Toaster position="top-center" richColors />
          <AppRouter />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
